/**
 * Autopilot on the Missions page (ADR 0073): a project's status as the hub reports it in
 * state.ext.missions.autopilot, or, for a project never set up, the same pick computed here with
 * Autopilot off. Demo mode (?mock=1) applies changes in the browser with the same rules.
 */
import { AUTOPILOT_DEFAULTS, applyAutopilotPatch, autopilotPick, autopilotWaitText } from '@orgus/protocol';
import type { AutopilotPatch, AutopilotSettings, AutopilotStatus, AutopilotWait, Id, MissionsState } from '@orgus/protocol';

const defaults = (): AutopilotSettings => ({ ...AUTOPILOT_DEFAULTS, kinds: [...AUTOPILOT_DEFAULTS.kinds], by: 'owner', at: 0 });

/** Missions that hold a slot: open and not finished. */
function openMissions(state: MissionsState, projectId: Id) {
  return state.missions.filter((m) => m.projectId === projectId && !m.closed && (m.state === 'planned' || m.state === 'running' || m.state === 'waiting'));
}

/** A status computed in the browser: for a project the hub has no settings for, and for demo mode. */
export function localAutopilotStatus(state: MissionsState, projectId: Id, settings: AutopilotSettings = defaults()): AutopilotStatus {
  const open = openMissions(state, projectId);
  const pick = autopilotPick({ projectId, items: state.items, links: state.links ?? [], kinds: settings.kinds, active: new Set(open.map((m) => m.itemId).filter((x): x is Id => !!x)), holds: {} });
  const running = open.filter((m) => m.autopilot).map((m) => m.id);
  const next = pick.ready[0] ?? null;
  const wait: AutopilotWait | null = !settings.on ? 'off' : running.length >= settings.maxMissions ? 'limit' : !next ? 'empty' : null;
  const base = { projectId, settings, running, spentToday: null, next, queue: pick.ready.slice(1, 11), skipped: pick.skipped.slice(0, 50), lastStart: null };
  return { ...base, wait, text: autopilotWaitText(wait, base) };
}

/** The project's Autopilot: the hub's status when it has one, else Autopilot off. */
export function autopilotOf(state: MissionsState, projectId: Id): AutopilotStatus {
  return state.autopilot?.find((a) => a.projectId === projectId) ?? localAutopilotStatus(state, projectId);
}

/** Demo mode: the slice with a project's settings changed. */
export function mockSetAutopilot(state: MissionsState, projectId: Id, patch: AutopilotPatch, now: number): MissionsState {
  const prev = state.autopilot?.find((a) => a.projectId === projectId)?.settings ?? defaults();
  const status = localAutopilotStatus(state, projectId, applyAutopilotPatch(prev, patch, 'owner', now));
  return { ...state, autopilot: [...(state.autopilot ?? []).filter((a) => a.projectId !== projectId), status] };
}

/** Which project the page sets Autopilot up for: the one chosen, or the only one there is. */
export function autopilotTarget(projectId: Id | null, projects: readonly { id: Id }[]): Id | null {
  return projectId ?? (projects.length === 1 ? projects[0]!.id : null);
}

/**
 * The Autopilot bars the page shows: the chosen project's, else every project whose Autopilot is
 * on or was ever set up (its settings were saved once). A project switched off keeps its bar, so
 * its switch is there to turn it on again; one never set up shows none.
 */
export function shownAutopilots(state: MissionsState, chosen: Id | null): AutopilotStatus[] {
  if (chosen) return [autopilotOf(state, chosen)];
  return (state.autopilot ?? []).filter((a) => a.settings.on || a.settings.at > 0);
}
